/* «Вы», близкие и персонажи движка шаблона (BotPeople) ↔ ссылки сервера (engine-kit, план M3 §2 S3; circle K3, reels K4, shoot
   people-inputs, motion K5, photo P4, collage K-YOU). BotPeople уже держит данные сервера: bridge/server-state.ts при входе (clearPeople,
   connectPeople, syncPeople — profile/people-server.ts), экран держит только свой выбор (sel: [{id, act, own, role}], «Вы» — ctx.me).
   Здесь — чистые кодировщики и разборщики без React и без запросов:
   - selfRef / selfCast: «Вы» — то, что стоит за «Вы» в аккаунте (свои фото или выбранный вариант), только когда «Вы» готов;
   - personRef: сохранённый ли человек, можно ли его выбрать (BotPeople selectable не читает — отказ с disabled_reason сервера), есть ли фото;
   - charRef: персонаж каталога (id из 'c:<id>');
   - строки состава `self | user_avatar:<id> | person:<id> | character:<id>` (cast, cast_<роль>), контракт `cast[]`
     {role_id, who, avatar_id?, person_id?, person?, templates?} (03-api §9.2, docs/59), объект `people {me, persons, chars}` (фото, видео;
     у «Фотосессии» — те же ключи сверху), один выбор `model / person_id / avatar_id / me` («Фотосессия товара»), и обратно — для повтора;
   - meState: объект «Вы» экрана для ctx.me (путь M0 bot-people.js: зеркало общего «Вы», своё у экрана — роль и «в кадре»);
   - близкий без сохранения («Только для этого …», Q-C2): контракт `person {name, rel, age, photo_upload_ids}` — только если форма его публикует;
   - watchPeople: bot:avatars / bot:me → P.paintForm(), когда ряд лиц изменился;
   - selfPicture: фото «Вы» (вариант или первое своё фото) или нейтральный силуэт — демо-лицо BC.YOU() в серверный режим не попадает (D12). */
import { studioFields, type StudioForm } from '../studio-api';
import { uiText } from '../../../i18n';

/** Запись близкого в BotPeople (people-server.ts person) */
export interface PeoplePerson {
  id: number;
  n: string;
  rel: string;
  age: string | null;
  photos: Array<{ url: string; id?: string; file?: Blob }>;
  version?: number;
  selectable?: boolean;
  disabled_reason?: string | null;
}
export interface PeopleVariant {
  id: number | string;
  vn?: string;
  name?: string;
  url?: string | null;
  status?: string;
}
export interface PeopleMe {
  src: 'photos' | 'avatar' | null;
  photos: Array<{ url: string; id?: string }>;
  avatar: number | string | null;
  pick: string | number;
  ready: boolean;
  variant: PeopleVariant | null;
}
/** То, что мост читает у window.BotPeople */
export interface PeopleModule {
  me: { get(): PeopleMe; variants(): PeopleVariant[] };
  store: { all(): PeoplePerson[] };
  actOf?(x: SelItem): string;
}
/** Выбор экрана (ctx.sel): человек — id, персонаж — 'c:<id>'; act — вариант экрана или 'own' со своим текстом own */
export interface SelItem {
  id: number | string;
  act?: string;
  own?: string;
  role?: string | null;
}
/** Исполнитель из P.snap().cast[].who и P.snap().chars */
export interface SnapPerformer {
  type: 'me' | 'avatar' | 'person' | 'char';
  id: number | string | null;
  act?: string;
}
export interface SnapCast {
  role: string;
  who: SnapPerformer[];
}
/** P.snap().pick: у экрана на одного и режима «модель» */
export interface SnapPick {
  who: 'none' | 'me' | 'person' | 'char' | 'tile';
  id: number | string | null;
}
export type SelfRef = { src: 'photos' } | { src: 'avatar'; avatar_id: number };
export type PersonRefusal = 'missing' | 'unsaved' | 'disabled' | 'photos';
export type PersonRef = { ok: true; person_id: number } | { ok: false; reason: PersonRefusal; text: string };
export interface CastEntry {
  role_id: string;
  who: 'me' | 'avatar' | 'person' | 'char';
  avatar_id?: number;
  person_id?: number;
  person?: UnsavedPerson;
  templates?: string[];
}
export interface UnsavedPerson {
  name: string;
  rel?: string;
  age?: string;
  photo_upload_ids: string[];
}
export interface PeopleContract {
  me?: SelfRef & { in?: boolean };
  persons: Array<{ person_id: number; action?: string }>;
  chars: Array<{ avatar_id: number }>;
}
export interface ModelChoice {
  model: 'none' | 'me' | 'person' | 'char' | 'gen';
  person_id?: number;
  avatar_id?: number;
  me?: SelfRef;
}
/** Объект «Вы» экрана для ctx.me (bot-people.js M0): src, photos, avatar зеркалит BotPeople; своё — role и in */
export interface MeState {
  src: 'photos' | 'avatar' | null;
  photos: unknown[];
  avatar: number | string | null;
  role: string | null;
  in: boolean;
}

export const peopleModule = (): PeopleModule | null => (window as unknown as { BotPeople?: PeopleModule }).BotPeople ?? null;
const positive = (value: unknown): number | null => {
  const n = typeof value === 'string' && /^[1-9][0-9]*$/.test(value) ? Number(value) : value;
  return typeof n === 'number' && Number.isSafeInteger(n) && n > 0 ? n : null;
};
const REF = /^(self|user_avatar:[1-9][0-9]*|person:[1-9][0-9]*|character:[1-9][0-9]*)$/;

/* ---------- ссылки ---------- */

/** «Вы» в заказ: выбранный вариант (avatar) или свои фото; «Вы» не готов — null */
export function selfRef(m: PeopleModule | null = peopleModule()): SelfRef | null {
  const me = m?.me.get();
  if (!me?.ready) return null;
  if (me.src === 'avatar') {
    const id = positive(me.avatar);
    return id ? { src: 'avatar', avatar_id: id } : null;
  }
  return me.src === 'photos' ? { src: 'photos' } : null;
}
/** «Вы» строкой состава: 'self' или 'user_avatar:<id>' */
export function selfCast(m: PeopleModule | null = peopleModule()): string | null {
  const ref = selfRef(m);
  return !ref ? null : ref.src === 'avatar' ? `user_avatar:${ref.avatar_id}` : 'self';
}
/** Близкий в заказ: только сохранённый в аккаунте, доступный для выбора (selectable, иначе причина сервера) и с фото */
export function personRef(id: unknown, m: PeopleModule | null = peopleModule()): PersonRef {
  const pid = positive(id);
  const person = pid ? m?.store.all().find(item => item.id === pid) : undefined;
  if (!pid || !person) return { ok: false, reason: pid ? 'unsaved' : 'missing', text: uiText('Этот человек не участвует') };
  if (person.selectable === false)
    return { ok: false, reason: 'disabled', text: uiText(person.disabled_reason || 'Этот человек не участвует') };
  if (!person.photos?.length) return { ok: false, reason: 'photos', text: uiText('Добавьте фото') };
  return { ok: true, person_id: pid };
}
/** id персонажа из ключа BotPeople ('c:<id>') или числа */
export function charId(key: unknown): number | null {
  return positive(typeof key === 'string' && key.startsWith('c:') ? key.slice(2) : key);
}
export function charRef(key: unknown): { avatar_id: number } | null {
  const id = charId(key);
  return id ? { avatar_id: id } : null;
}

/* ---------- кодировщики ---------- */

/** Ключ BotPeople ('me', id близкого, 'c:<id>') → строка состава; не годится в заказ — null */
export function castRef(key: unknown, m: PeopleModule | null = peopleModule()): string | null {
  if (key === 'me') return selfCast(m);
  if (typeof key === 'string' && key.startsWith('c:')) {
    const id = charId(key);
    return id ? `character:${id}` : null;
  }
  const person = personRef(key, m);
  return person.ok ? `person:${person.person_id}` : null;
}
/** Исполнитель снимка → строка состава */
export function performerRef(p: SnapPerformer, m: PeopleModule | null = peopleModule()): string | null {
  if (p.type === 'me') return 'self';
  if (p.type === 'avatar') {
    const id = positive(p.id);
    return id ? `user_avatar:${id}` : null;
  }
  if (p.type === 'char') {
    const id = positive(p.id);
    return id ? `character:${id}` : null;
  }
  return castRef(p.id, m);
}
/** Строка состава → запись контракта cast[] (как reels-cast.ts refToEntry) */
export function refEntry(roleId: string, ref: string, templates: string[] = []): CastEntry | null {
  if (!REF.test(ref)) return null;
  const [kind, raw] = ref.split(':');
  const id = Number(raw);
  const base: CastEntry =
    kind === 'user_avatar'
      ? { role_id: roleId, who: 'avatar', avatar_id: id }
      : kind === 'person'
        ? { role_id: roleId, who: 'person', person_id: id }
        : kind === 'character'
          ? { role_id: roleId, who: 'char', avatar_id: id }
          : { role_id: roleId, who: 'me' };
  return templates.length ? { ...base, templates } : base;
}
/** Роли снимка (P.snap().cast) → строки состава по ролям (поля cast_<роль>); дубли и не годные в заказ отброшены */
export function castRefs(cast: SnapCast[], m: PeopleModule | null = peopleModule()): Record<string, string[]> {
  const seen = new Set<string>();
  const out: Record<string, string[]> = {};
  for (const row of cast) {
    out[row.role] ??= [];
    for (const p of row.who) {
      const ref = performerRef(p, m);
      if (ref && !seen.has(ref)) {
        seen.add(ref);
        out[row.role].push(ref);
      }
    }
  }
  return out;
}
/** Роли снимка → контракт cast[]; templates(роль) — шаблоны набора, где роль играет (рилс с несколькими шаблонами) */
export function castEntries(cast: SnapCast[], o: { templates?(roleId: string): string[]; m?: PeopleModule | null } = {}): CastEntry[] {
  const refs = castRefs(cast, o.m === undefined ? peopleModule() : o.m);
  return Object.entries(refs).flatMap(([role, list]) =>
    list.map(ref => refEntry(role, ref, o.templates?.(role) ?? [])).filter((entry): entry is CastEntry => !!entry),
  );
}
const actOf = (x: SelItem, m: PeopleModule | null) =>
  (m?.actOf ? m.actOf(x) : x.act === 'own' ? (x.own ?? '').trim() : (x.act ?? '')).trim();
/** Выбор ряда без ролей → объект `people {me, persons, chars}`. me — «Вы» в кадре (P.snap().me.in) и готов; withIn — писать me.in (фото, видео) */
export function peopleOf(o: { me: boolean; sel: SelItem[]; withIn?: boolean; m?: PeopleModule | null }): PeopleContract {
  const m = o.m === undefined ? peopleModule() : o.m;
  const me = o.me ? selfRef(m) : null;
  const persons: PeopleContract['persons'] = [];
  const chars: PeopleContract['chars'] = [];
  for (const x of o.sel) {
    if (typeof x.id === 'string' && x.id.startsWith('c:')) {
      const ref = charRef(x.id);
      if (ref && !chars.some(c => c.avatar_id === ref.avatar_id)) chars.push(ref);
      continue;
    }
    const ref = personRef(x.id, m);
    if (!ref.ok || persons.some(p => p.person_id === ref.person_id)) continue;
    const action = actOf(x, m);
    persons.push(action ? { person_id: ref.person_id, action } : { person_id: ref.person_id });
  }
  return { ...(me ? { me: o.withIn ? { ...me, in: true } : me } : {}), persons, chars };
}
/** Один выбор (P.snap().pick, режим «модель») → model / person_id / avatar_id / me (PShootOrderForm pickModel); tile — своя плитка экрана (gen) */
export function modelOf(pick: SnapPick | null, m: PeopleModule | null = peopleModule()): ModelChoice {
  if (!pick || pick.who === 'none') return { model: 'none' };
  if (pick.who === 'tile') return { model: 'gen' };
  if (pick.who === 'me') {
    const me = selfRef(m);
    return me ? { model: 'me', me } : { model: 'none' };
  }
  if (pick.who === 'person') {
    const ref = personRef(pick.id, m);
    return ref.ok ? { model: 'person', person_id: ref.person_id } : { model: 'none' };
  }
  const char = charRef(pick.id);
  return char ? { model: 'char', avatar_id: char.avatar_id } : { model: 'none' };
}

/* ---------- разборщики (повтор, «Изменить») ---------- */

export type DecodedRef = { who: 'me' } | { who: 'avatar'; id: number } | { who: 'person'; id: number } | { who: 'char'; id: number };
export function parseRef(ref: unknown): DecodedRef | null {
  if (typeof ref !== 'string' || !REF.test(ref)) return null;
  if (ref === 'self') return { who: 'me' };
  const [kind, raw] = ref.split(':');
  const id = Number(raw);
  return kind === 'user_avatar' ? { who: 'avatar', id } : kind === 'person' ? { who: 'person', id } : { who: 'char', id };
}
/** Строка состава → ключ BotPeople: «Вы» (свои фото или вариант — выбор варианта остаётся за аккаунтом), id близкого, 'c:<id>' */
export function refKey(ref: unknown): 'me' | number | string | null {
  const value = parseRef(ref);
  if (!value) return null;
  if (value.who === 'me' || value.who === 'avatar') return 'me';
  return value.who === 'person' ? value.id : `c:${value.id}`;
}
/** Запись контракта cast[] → строка состава */
export function entryRef(entry: unknown): string | null {
  const e = entry && typeof entry === 'object' ? (entry as Record<string, unknown>) : {};
  if (e.who === 'me') return 'self';
  const id = positive(e.who === 'person' ? e.person_id : e.avatar_id);
  if (!id) return null;
  return e.who === 'avatar' ? `user_avatar:${id}` : e.who === 'person' ? `person:${id}` : e.who === 'char' ? `character:${id}` : null;
}
/** Действие из заказа → act/own экрана: вариант экрана (acts) или свой текст */
const actFrom = (action: unknown, acts: string[]): Pick<SelItem, 'act' | 'own'> =>
  typeof action !== 'string' || !action ? {} : acts.includes(action) ? { act: action } : { act: 'own', own: action };
/** Состав по ролям (строки или контракт cast[]) → выбор BotPeople с ролями и роль «Вы» (её экран кладёт в meState) */
export function castSel(cast: Record<string, string[]> | unknown[]): { sel: SelItem[]; meRole: string | null } {
  const rows: Array<[string, string]> = Array.isArray(cast)
    ? cast.flatMap(entry => {
        const role = (entry as { role_id?: unknown })?.role_id;
        const ref = entryRef(entry);
        return typeof role === 'string' && ref ? [[role, ref] as [string, string]] : [];
      })
    : Object.entries(cast).flatMap(([role, refs]) => refs.map(ref => [role, ref] as [string, string]));
  const sel: SelItem[] = [];
  let meRole: string | null = null;
  for (const [role, ref] of rows) {
    const key = refKey(ref);
    if (key === null) continue;
    if (key === 'me') meRole ??= role;
    else if (!sel.some(x => x.id === key)) sel.push({ id: key, role });
  }
  return { sel, meRole };
}
/** Объект `people` (или ключи me/persons/chars сверху) → выбор ряда без ролей и «Вы» в кадре */
export function peopleSel(
  raw: { me?: unknown; persons?: unknown; chars?: unknown },
  acts: string[] = [],
): { sel: SelItem[]; meIn: boolean } {
  const sel: SelItem[] = [];
  for (const p of Array.isArray(raw.persons) ? raw.persons : []) {
    const id = positive((p as { person_id?: unknown })?.person_id);
    if (id && !sel.some(x => x.id === id)) sel.push({ id, ...actFrom((p as { action?: unknown }).action, acts), role: null });
  }
  for (const c of Array.isArray(raw.chars) ? raw.chars : []) {
    const id = positive((c as { avatar_id?: unknown })?.avatar_id);
    if (id && !sel.some(x => x.id === `c:${id}`)) sel.push({ id: `c:${id}`, role: null });
  }
  const me = raw.me && typeof raw.me === 'object' ? (raw.me as { src?: unknown; in?: unknown }) : null;
  return { sel, meIn: !!me && (me.src === 'photos' || me.src === 'avatar') && me.in !== false };
}
/** Один выбор из заказа (model / person_id / avatar_id) → P.snap().pick */
export function pickFrom(inputs: { model?: unknown; person_id?: unknown; avatar_id?: unknown }): SnapPick {
  if (inputs.model === 'me') return { who: 'me', id: null };
  if (inputs.model === 'gen') return { who: 'tile', id: 'gen' };
  if (inputs.model === 'person' && positive(inputs.person_id)) return { who: 'person', id: positive(inputs.person_id) };
  if (inputs.model === 'char' && positive(inputs.avatar_id)) return { who: 'char', id: positive(inputs.avatar_id) };
  return { who: 'none', id: null };
}
/** Объект «Вы» экрана для ctx.me: src, photos, avatar BotPeople заполнит сам (src null — без перезаписи общего «Вы»); role и in — экрана */
export function meState(o: { in?: boolean; role?: string | null } = {}): MeState {
  return { src: null, photos: [], avatar: null, role: o.role ?? null, in: !!o.in };
}

/* ---------- близкий без сохранения (Q-C2) ---------- */

/** Форма публикует контракт `cast[].person` (свойство person у поля состава) — иначе вариант «Только для этого …» скрыт */
export function publishesUnsaved(form: StudioForm | null | undefined, key = 'cast'): boolean {
  const field = studioFields(form).find(item => item.key === key) as { properties?: Array<{ key?: unknown }> } | undefined;
  return Array.isArray(field?.properties) && field.properties.some(item => item?.key === 'person');
}
/** Близкий без сохранения в контракт: имя, статус, возраст и загруженные фото (1…5); без фото или имени — null */
export function unsavedPerson(p: Pick<PeoplePerson, 'n' | 'rel' | 'age'>, photoUploadIds: string[]): UnsavedPerson | null {
  const name = (p.n ?? '').trim();
  const ids = photoUploadIds.filter(id => /^[a-f0-9-]{36}$/i.test(id)).slice(0, 5);
  if (!name || !ids.length) return null;
  return { name, ...(p.rel ? { rel: p.rel } : {}), ...(p.age ? { age: p.age } : {}), photo_upload_ids: ids };
}

/* ---------- ряд лиц и «Вы» картинкой ---------- */

/** Подпись ряда лиц: «Вы» (готов, чем стоит, выбор), варианты и близкие (имя, фото, доступность) */
export function peopleSignature(m: PeopleModule | null = peopleModule()): string {
  if (!m) return '';
  const me = m.me.get();
  return JSON.stringify([
    [me.src, me.ready, String(me.pick), me.photos.length, me.photos[0]?.url ?? ''],
    m.me.variants().map(v => [String(v.id), v.name ?? '', v.status ?? '', v.url ?? '']),
    m.store.all().map(p => [p.id, p.n, p.rel, p.age, p.photos.length, p.photos[0]?.url ?? '', p.selectable !== false]),
  ]);
}
/** bot:avatars и bot:me (syncPeople, листы «Вы», варианты) → P.paintForm(), когда ряд лиц изменился. → отписка */
export function watchPeople(o: { paint(): void; mounted?(): boolean; changed?(): void; m?(): PeopleModule | null }): () => void {
  const read = () => peopleSignature(o.m ? o.m() : peopleModule());
  let last = read();
  const on = () => {
    const next = read();
    if (next === last) return;
    last = next;
    if (o.mounted && !o.mounted()) return;
    o.paint();
    o.changed?.();
  };
  document.addEventListener('bot:avatars', on);
  document.addEventListener('bot:me', on);
  return () => {
    document.removeEventListener('bot:avatars', on);
    document.removeEventListener('bot:me', on);
  };
}
/** Нейтральный силуэт (форма силуэта взрослого bot-people.js GLYPH, цвет подписей шаблона): «Вы» без фото — не демо-лицо */
export const SILHOUETTE =
  'data:image/svg+xml,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 56 56"><g fill="#8e8e93"><circle cx="28" cy="22" r="8.5"/><path d="M11 57c0-12 7.6-18.5 17-18.5S45 45 45 57z"/></g></svg>',
  );
/** Картинка «Вы»: превью выбранного варианта (адрес сервера) или первое своё фото; нет — силуэт (D12: BC.YOU() не показывается) */
export function selfPicture(m: PeopleModule | null = peopleModule()): string {
  const me = m?.me.get();
  if (!me?.ready) return SILHOUETTE;
  const url = me.src === 'avatar' ? me.variant?.url : me.photos[0]?.url;
  return url || SILHOUETTE;
}
